
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GroupedReview } from "@/types/review";
import { Star, MapPin, Calendar, Bed, Bath, Home, Check, CircleParking } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";

interface AddressReviewCardProps {
  groupedReview: GroupedReview;
}

// Currency mapping based on country
const getCurrencyInfo = (address: string) => {
  // Simple detection based on address content - you might want to improve this logic
  if (address.includes('台灣') || address.includes('台北') || address.includes('新北') || address.includes('台中') || address.includes('台南') || address.includes('高雄')) {
    return { symbol: 'NT$', code: 'TWD' };
  }
  if (address.includes('Canada') || address.includes('Vancouver') || address.includes('Toronto') || address.includes('Montreal')) {
    return { symbol: 'CA$', code: 'CAD' };
  }
  // Default to CAD
  return { symbol: 'CA$', code: 'CAD' };
};

const AddressReviewCard = ({ groupedReview }: AddressReviewCardProps) => {
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  
  const DEFAULT_IMAGE = "https://cwuqmrrfvwddicutkrcc.supabase.co/storage/v1/object/public/review_images//logo%20(1).png";
  const currencyInfo = getCurrencyInfo(groupedReview.address);

  const getRatingColor = (rating: number): string => {
    if (rating <= 2) return 'text-rating-low';
    if (rating === 3) return 'text-rating-mid';
    return 'text-rating-high';
  };
  
  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-CA');
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };
  
  // Get display name (community name first, then display_address for privacy, then address)
  const displayName = groupedReview.communityName || groupedReview.display_address || groupedReview.address;

  // Get display address (prioritize display_address for privacy)
  const displayAddress = groupedReview.display_address || groupedReview.address;

  // Translate housing type
  const translateHousingType = (type: string): string => {
    return t(`form.housingTypes.${type}`) || type;
  };
  
  return (
    <Card className="overflow-hidden hover:shadow-md transition-colors w-full mx-auto">
      <div className="flex flex-col md:flex-row min-h-[200px]">
        {/* Image section - left side */}
        <div className="w-full md:w-1/4 max-h-60 md:h-auto overflow-hidden">
          <div className="h-40 md:h-full w-full relative">
            <img
              src={groupedReview.firstImageUrl || DEFAULT_IMAGE}
              alt="Property"
              className="w-full h-full object-contain bg-white p-1"
              onError={(e) => {
                (e.target as HTMLImageElement).src = DEFAULT_IMAGE;
              }}
            />
          </div>
        </div>

        {/* Content section - right side */}
        <div className="flex-1 w-full md:w-3/4 flex flex-col">
          <CardContent className="p-3 md:p-4 flex-grow">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-start justify-between">
                <div className="max-w-[80%]">
                  <h3 className="text-base font-bold line-clamp-1">{displayName}</h3>
                  <div className="flex items-center gap-1 text-muted-foreground text-xs">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="line-clamp-2">{displayAddress}</span>
                  </div>
                  {groupedReview.lastReviewDate && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Calendar className="h-3 w-3 flex-shrink-0" />
                      <span>{t('latestReview')}: {formatDate(groupedReview.lastReviewDate)}</span>
                    </div>
                  )}
                </div>
                
                <div className={`flex items-center gap-0.5 font-bold ${getRatingColor(groupedReview.averageRating)}`}>
                  <Star className="h-4 w-4 fill-current" />
                  <span className="text-lg">{groupedReview.averageRating.toFixed(1)}</span>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                {t('totalReviews', { count: groupedReview.totalReviews })}
              </p>
              
              <div className="flex flex-wrap gap-2 text-xs mt-2">
                {groupedReview.minBedrooms > 0 && (
                  <div className="flex items-center gap-1">
                    <Bed className="h-3 w-3 text-muted-foreground" />
                    <span>{groupedReview.minBedrooms === groupedReview.maxBedrooms ? 
                      groupedReview.minBedrooms : `${groupedReview.minBedrooms}-${groupedReview.maxBedrooms}`}</span>
                  </div>
                )}
                
                {groupedReview.minBathrooms > 0 && (
                  <div className="flex items-center gap-1">
                    <Bath className="h-3 w-3 text-muted-foreground" />
                    <span>{groupedReview.minBathrooms === groupedReview.maxBathrooms ? 
                      groupedReview.minBathrooms : `${groupedReview.minBathrooms}-${groupedReview.maxBathrooms}`}</span>
                  </div>
                )}
                
                {groupedReview.housingTypes && groupedReview.housingTypes.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Home className="h-3 w-3 text-muted-foreground" />
                    <span className="line-clamp-1 max-w-[180px]">
                      {groupedReview.housingTypes.map(type => translateHousingType(type)).join(', ')}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-1.5 mt-2">
                {groupedReview.hasImmigrantFriendly && (
                  <Badge variant="outline" className="rounded-full px-2 py-0.5 text-xs bg-gray-100">
                    {t('immigrantFriendly')}
                    <Check className="h-3 w-3 ml-1 text-green-500" />
                  </Badge>
                )}
                {groupedReview.hasPetAllowed && (
                  <Badge variant="outline" className="rounded-full px-2 py-0.5 text-xs bg-gray-100">
                    {t('petAllowed')}
                    <Check className="h-3 w-3 ml-1 text-green-500" />
                  </Badge>
                )}
                {groupedReview.hasEasyApply && (
                  <Badge variant="outline" className="rounded-full px-2 py-0.5 text-xs bg-gray-100">
                    {t('easyApply')}
                    <Check className="h-3 w-3 ml-1 text-green-500" />
                  </Badge>
                )}
                {groupedReview.hasFlexibleContract && (
                  <Badge variant="outline" className="rounded-full px-2 py-0.5 text-xs bg-gray-100">
                    {t('flexibleContract')}
                    <Check className="h-3 w-3 ml-1 text-green-500" />
                  </Badge>
                )}
                {groupedReview.hasParkingAvailable && (
                  <Badge variant="outline" className="rounded-full px-2 py-0.5 text-xs bg-gray-100">
                    {language === 'zh-TW' ? '停車位' : 'Parking'}
                    <Check className="h-3 w-3 ml-1 text-green-500" />
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="px-3 md:px-4 py-2 md:py-3 bg-muted/50 mt-auto">
            <div className="w-full flex items-center justify-between">
              <div className="text-xs md:text-sm font-medium truncate mr-2">
                {t('rentRange')}: {currencyInfo.symbol} {formatCurrency(groupedReview.minRent)}
                {groupedReview.minRent !== groupedReview.maxRent && 
                  ` - ${formatCurrency(groupedReview.maxRent)}`}
              </div>
              
              <Button asChild variant="default" size="sm" className="h-8 text-xs px-3">
                <Link 
                  to={`/address/${encodeURIComponent(displayAddress)}`}
                >
                  {t('viewDetails')}
                </Link>
              </Button>
            </div>
          </CardFooter>
        </div>
      </div>
    </Card>
  );
};

export default AddressReviewCard;
